import React, { useState, useEffect } from 'react';
import ApiClient from '../utils/ApiClient';
import AuditListTable from '../components/AuditReview/AuditListTable';
import AuditDetailModal from '../components/AuditReview/AuditDetailModal';

const AuditReview = () => {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAuditId, setSelectedAuditId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [locationFilter, setLocationFilter] = useState('');
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    fetchAudits();
  }, [limit, locationFilter]);

  const fetchAudits = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `/api/inventory/audits/recent?limit=${limit}`;
      if (locationFilter) {
        url += `&location=${encodeURIComponent(locationFilter)}`;
      }

      const response = await ApiClient.get(url);

      if (response.data.success) {
        setAudits(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load audits');
      }
    } catch (err) {
      setError('Failed to load audits: ' + err.message);
      console.error('Error fetching audits:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (auditSessionId) => {
    setSelectedAuditId(auditSessionId);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedAuditId(null);
  };

  const handleRefresh = () => {
    fetchAudits();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Audit Review</h1>
        <p className="text-gray-600">Review inventory audit history and details</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location Filter
            </label>
            <input
              type="text"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              placeholder="Filter by location..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Results
            </label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Audit List Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading audits...</p>
        </div>
      ) : audits.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-600">No audits found</p>
          {locationFilter && (
            <p className="text-sm text-gray-500 mt-2">
              Try adjusting your location filter
            </p>
          )}
        </div>
      ) : (
        <AuditListTable
          audits={audits}
          onViewDetails={handleViewDetails}
        />
      )}

      {/* Audit Detail Modal */}
      {showDetailModal && selectedAuditId && (
        <AuditDetailModal
          auditSessionId={selectedAuditId}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default AuditReview;
